import ConfirmModal from '@/components/confirm-modal'
import { cancelSalesFlowRun, getSalesFlow, getSalesFlowRun, type SalesFlowNode, type SalesFlowRun } from '@/lib/api/sales/flows-api'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'
import { MdClose } from 'react-icons/md'
import { toast } from 'react-toastify'
import { FLOW_RUN_STATUS } from '../flow-meta'
import RunLogTimeline from './run-log-timeline'

/** Detalhe de uma execução: passos, motivo de parada e cancelamento. */
export default function RunDetailModal({ runId, onClose, onChanged }: { runId: number; onClose: () => void; onChanged?: () => void }) {
    const [run, setRun] = useState<SalesFlowRun | null>(null)
    const [nodes, setNodes] = useState<SalesFlowNode[]>([])
    const [confirmCancel, setConfirmCancel] = useState(false)
    const [cancelling, setCancelling] = useState(false)

    useEffect(() => {
        getSalesFlowRun(runId).then(async res => {
            if (!res.success) {
                toast.error(res.error || 'Execução não encontrada')
                onClose()
                return
            }
            setRun(res.result)
            const flow = await getSalesFlow(res.result.flowId)
            if (flow.success) setNodes(flow.result.nodes ?? [])
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runId])

    const cancel = async () => {
        setCancelling(true)
        const res = await cancelSalesFlowRun(runId)
        setCancelling(false)
        setConfirmCancel(false)
        if (!res.success) {
            toast.error(res.error || 'Não foi possível cancelar')
            return
        }
        toast.success('Execução cancelada')
        setRun(res.result)
        onChanged?.()
    }

    const status = run ? FLOW_RUN_STATUS[run.status] : null
    const active = run?.status === 'RUNNING' || run?.status === 'WAITING'

    return (
        <dialog className="modal modal-open">
            <div className="modal-box max-w-2xl">
                <div className="mb-5 flex items-start justify-between gap-4">
                    <div>
                        <h3 className="text-lg font-semibold">Execução #{runId}</h3>
                        {run && (
                            <p className="mt-1 text-sm text-base-content/60">
                                {run.flow?.name} · iniciada em {format(new Date(run.startedAt), 'dd/MM/yyyy HH:mm')}
                            </p>
                        )}
                    </div>
                    <button type="button" className="btn btn-ghost btn-sm btn-square" onClick={onClose} aria-label="Fechar">
                        <MdClose />
                    </button>
                </div>

                {!run ? (
                    <div className="flex justify-center py-10">
                        <span className="loading loading-spinner" />
                    </div>
                ) : (
                    <div className="space-y-5">
                        <div className="flex flex-wrap items-center gap-2">
                            {status && <span className={`badge ${status.badge}`}>{status.label}</span>}
                            {run.dryRun && <span className="badge badge-info badge-outline">Simulação</span>}
                            {run.status === 'WAITING' && run.resumeAt && (
                                <span className="text-sm text-base-content/60">retoma em {format(new Date(run.resumeAt), 'dd/MM HH:mm')}</span>
                            )}
                        </div>
                        {run.lastError && <div className="alert py-2 text-sm">{run.lastError}</div>}
                        <RunLogTimeline logs={run.logs ?? []} nodes={nodes} />
                    </div>
                )}

                <div className="modal-action">
                    {active && (
                        <button type="button" className="btn btn-ghost text-error" onClick={() => setConfirmCancel(true)}>
                            Cancelar execução
                        </button>
                    )}
                    <button type="button" className="btn" onClick={onClose}>
                        Fechar
                    </button>
                </div>
            </div>
            <div className="modal-backdrop" onClick={onClose} />

            <ConfirmModal
                isOpen={confirmCancel}
                onClose={() => setConfirmCancel(false)}
                onConfirm={cancel}
                loading={cancelling}
                title="Cancelar execução"
                message="O lead sai do fluxo agora e nenhum passo seguinte é executado."
                confirmButtonText="Cancelar execução"
                cancelButtonText="Voltar"
            />
        </dialog>
    )
}
